import React, { useState, useEffect } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { DEAL_STAGES, EDITABLE_STAGES, getStageName, getSearchUrls, parseItems, parseItemLine } from '../../utils/dealConstants'
import { useAuth } from '../../hooks/useAuth'
import PinButton from '../shared/PinButton'

// Helper to get tracking URL based on carrier
function getTrackingUrl(trackingNumber, carrier) {
  if (!trackingNumber || !carrier) return null
  const carrierLower = carrier.toLowerCase()

  if (carrierLower.includes('royal')) {
    return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
  }
  if (carrierLower.includes('parcel')) {
    if (trackingNumber.toUpperCase().startsWith('PFW')) {
      return `https://send.parcelforce.com/tracking/${trackingNumber.toLowerCase()}`
    }
    return `https://www.royalmail.com/track-your-item#/tracking-results/${trackingNumber}`
  }
  if (carrierLower.includes('fedex')) {
    return `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`
  }
  if (carrierLower.includes('dhl')) {
    return `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${trackingNumber}`
  }
  return null
}

function DealDetailModal({ deal, onClose, onDealUpdated }) {
  const { user } = useAuth()
  const isAdmin = user?.is_admin

  // Deal data (refreshed from API)
  const [selectedDeal, setSelectedDeal] = useState(deal)

  // Stage changing
  const [changingStage, setChangingStage] = useState(false)

  // Parsed items editing
  const [editingParsedItems, setEditingParsedItems] = useState(false)
  const [parsedItemsValue, setParsedItemsValue] = useState(deal?.parsed_items || '')
  const [savingParsedItems, setSavingParsedItems] = useState(false)

  // Collection date editing
  const [editingCollectionDate, setEditingCollectionDate] = useState(false)
  const [collectionDateValue, setCollectionDateValue] = useState(deal?.collection_date || '')
  const [savingCollectionDate, setSavingCollectionDate] = useState(false)

  // Collection link editing
  const [editingCollectionLink, setEditingCollectionLink] = useState(false)
  const [collectionLinkValue, setCollectionLinkValue] = useState(deal?.collection_link || '')
  const [savingCollectionLink, setSavingCollectionLink] = useState(false)
  const [regeneratingToken, setRegeneratingToken] = useState(false)

  // Pricing log editing
  const [pricingLogExpanded, setPricingLogExpanded] = useState(false)
  const [editingPricingLog, setEditingPricingLog] = useState(false)
  const [pricingLogValue, setPricingLogValue] = useState('')
  const [savingPricingLog, setSavingPricingLog] = useState(false)
  const [paymentDetailsExpanded, setPaymentDetailsExpanded] = useState(false)
  const [addressDetailsExpanded, setAddressDetailsExpanded] = useState(false)

  // Value editing
  const [editingValue, setEditingValue] = useState(false)
  const [valueInput, setValueInput] = useState(deal?.amount || '')
  const [savingValue, setSavingValue] = useState(false)

  // Payment form
  const [settingPayment, setSettingPayment] = useState(false)
  const [paymentForm, setPaymentForm] = useState({
    method: '',
    paypalEmail: '',
    bankName: '',
    bankAccount: '',
    bankSort: ''
  })
  const [savingPayment, setSavingPayment] = useState(false)

  // Deal images (R2)
  const [dealImages, setDealImages] = useState([])
  const [loadingDealImages, setLoadingDealImages] = useState(false)
  const [dealImagesExpanded, setDealImagesExpanded] = useState(false)

  // Email viewing
  const [expandedEmailDealId, setExpandedEmailDealId] = useState(null)
  const [customerEmails, setCustomerEmails] = useState({})
  const [loadingCustomerEmails, setLoadingCustomerEmails] = useState({})
  const [emailDetailModal, setEmailDetailModal] = useState(null)

  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, images: [], currentIndex: 0 })

  // Create Ticket modal state
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [ticketDeal, setTicketDeal] = useState(null)
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketMessage, setTicketMessage] = useState('')
  const [sendingTicket, setSendingTicket] = useState(false)

  // Fetch fresh deal data on open
  useEffect(() => {
    if (!deal?.id) return
    setSelectedDeal(deal)
    setParsedItemsValue(deal.parsed_items || '')
    setEditingParsedItems(false)
    setPricingLogExpanded(false)
    setPaymentDetailsExpanded(false)
    setDealImages([])
    setDealImagesExpanded(false)

    async function fetchDeal() {
      try {
        const response = await api.get(`/api/deals/${deal.id}`)
        if (response.deal) {
          setSelectedDeal(response.deal)
          setParsedItemsValue(response.deal.parsed_items || '')
        }
      } catch (e) {
        console.error('Failed to refresh deal:', e)
      }
    }
    fetchDeal()
  }, [deal?.id])

  // Lightbox functions
  function openLightbox(images, startIndex = 0) {
    setLightbox({ open: true, images, currentIndex: startIndex })
  }

  function closeLightbox() {
    setLightbox({ open: false, images: [], currentIndex: 0 })
  }

  function nextImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }))
  }

  function prevImage() {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }))
  }

  // Lightbox keyboard navigation
  useEffect(() => {
    if (!lightbox.open) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLightbox()
      if (e.key === 'ArrowLeft') prevImage()
      if (e.key === 'ArrowRight') nextImage()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightbox.open])

  // Close modal handler
  function handleClose() {
    setEditingParsedItems(false)
    setPricingLogExpanded(false)
    setPaymentDetailsExpanded(false)
    setSettingPayment(false)
    setPaymentForm({ method: '', paypalEmail: '', bankName: '', bankAccount: '', bankSort: '' })
    setAddressDetailsExpanded(false)
    setDealImages([])
    setDealImagesExpanded(false)
    setExpandedEmailDealId(null)
    setEmailDetailModal(null)
    onClose()
  }

  // Toggle email list for a contact
  async function toggleEmailExpand(dealId, email) {
    if (expandedEmailDealId === dealId) {
      setExpandedEmailDealId(null)
      return
    }

    setExpandedEmailDealId(dealId)

    if (customerEmails[dealId]) return

    setLoadingCustomerEmails(prev => ({ ...prev, [dealId]: true }))

    try {
      const response = await api.get(`/api/emails?email=${encodeURIComponent(email)}`)
      setCustomerEmails(prev => ({ ...prev, [dealId]: response.emails || [] }))
    } catch (error) {
      console.error('Error fetching emails:', error)
      setCustomerEmails(prev => ({ ...prev, [dealId]: [] }))
    } finally {
      setLoadingCustomerEmails(prev => ({ ...prev, [dealId]: false }))
    }
  }

  // Fetch full email details
  async function fetchEmailDetail(messageId, source) {
    try {
      const response = await api.get(`/api/emails/${messageId}?source=${source}`)
      if (response.email) {
        setEmailDetailModal(response.email)
      }
    } catch (error) {
      console.error('Error fetching email detail:', error)
      alert('Failed to load email')
    }
  }

  // Change deal stage
  async function changeDealStage(newStageId) {
    const stageName = getStageName(newStageId)
    if (!confirm(`Change deal stage to "${stageName}"?`)) return

    setChangingStage(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}/status`, { dealstage: newStageId })
      setSelectedDeal(prev => ({ ...prev, dealstage: newStageId }))
      if (onDealUpdated) onDealUpdated()
      alert(`Deal stage changed to "${stageName}"`)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setChangingStage(false)
    }
  }

  // Save parsed items
  async function saveParsedItems() {
    setSavingParsedItems(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { parsed_items: parsedItemsValue })
      setSelectedDeal(prev => ({ ...prev, parsed_items: parsedItemsValue }))
      setEditingParsedItems(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingParsedItems(false)
    }
  }

  async function saveValue() {
    const numericValue = parseFloat(valueInput)
    if (isNaN(numericValue) || numericValue < 0) {
      alert('Please enter a valid amount')
      return
    }
    setSavingValue(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { amount: String(numericValue) })
      setSelectedDeal(prev => ({ ...prev, amount: String(numericValue) }))
      setEditingValue(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingValue(false)
    }
  }

  async function saveCollectionDate() {
    setSavingCollectionDate(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { collection_date: collectionDateValue || '' })
      setSelectedDeal(prev => ({ ...prev, collection_date: collectionDateValue }))
      setEditingCollectionDate(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingCollectionDate(false)
    }
  }

  async function saveCollectionLink() {
    setSavingCollectionLink(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { collection_link: collectionLinkValue })
      setSelectedDeal(prev => ({ ...prev, collection_link: collectionLinkValue }))
      setEditingCollectionLink(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSavingCollectionLink(false)
    }
  }

  async function regenerateCollectionToken() {
    if (!confirm('This will generate a new collection token and update the collection link. Continue?')) return
    setRegeneratingToken(true)
    try {
      const res = await api.post(`/api/deals/${selectedDeal.id}/regenerate-collection-token`)
      setSelectedDeal(prev => ({ ...prev, collection_link: res.collection_link }))
      setCollectionLinkValue(res.collection_link)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setRegeneratingToken(false)
    }
  }

  async function savePricingLog() {
    setSavingPricingLog(true)
    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { claude_pricing_log: pricingLogValue })
      setSelectedDeal(prev => ({ ...prev, claude_pricing_log: pricingLogValue }))
      setEditingPricingLog(false)
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Error saving pricing log: ${error.message}`)
    } finally {
      setSavingPricingLog(false)
    }
  }

  // Open create ticket modal
  function openTicketModal(targetDeal, e) {
    if (e) e.stopPropagation()
    const d = targetDeal || selectedDeal
    const customerEmail = d?.seller_email || d?.contact_email
    if (!customerEmail) {
      alert('No email address found for this deal')
      return
    }
    setTicketDeal(d)
    setTicketSubject(`Re: ${d?.dealname || 'Your Deal'}`)
    setTicketMessage('')
    setShowTicketModal(true)
  }

  // Send ticket/email
  async function sendTicket() {
    const customerEmail = ticketDeal?.seller_email || ticketDeal?.contact_email
    if (!customerEmail || !ticketSubject || !ticketMessage) {
      alert('Please fill in subject and message')
      return
    }

    setSendingTicket(true)
    try {
      await api.post('/api/tickets/create-from-deal', {
        dealId: ticketDeal.id,
        toEmail: customerEmail,
        toName: ticketDeal.customer_name || '',
        subject: ticketSubject,
        message: ticketMessage
      })
      alert('Email sent and ticket created')
      setShowTicketModal(false)
      setTicketDeal(null)
      setTicketSubject('')
      setTicketMessage('')
    } catch (error) {
      alert(`Failed to send: ${error.message}`)
    } finally {
      setSendingTicket(false)
    }
  }

  // Save payment details
  async function savePaymentDetails() {
    if (!paymentForm.method) {
      alert('Please select a payment method')
      return
    }
    if (paymentForm.method === 'paypal' && !paymentForm.paypalEmail) {
      alert('Please enter PayPal email address')
      return
    }
    if (paymentForm.method === 'bank' && (!paymentForm.bankName || !paymentForm.bankAccount || !paymentForm.bankSort)) {
      alert('Please fill in all bank details')
      return
    }

    setSavingPayment(true)
    try {
      const updateData = {}
      if (paymentForm.method === 'paypal') {
        updateData.payment_paypal_email = paymentForm.paypalEmail
        updateData.payment_bank_name = ''
        updateData.payment_bank_account = ''
        updateData.payment_bank_sort = ''
      } else {
        updateData.payment_bank_name = paymentForm.bankName
        updateData.payment_bank_account = paymentForm.bankAccount
        updateData.payment_bank_sort = paymentForm.bankSort
        updateData.payment_paypal_email = ''
      }

      await api.patch(`/api/deals/${selectedDeal.id}`, updateData)

      const newDeal = {
        ...selectedDeal,
        payment_method: paymentForm.method,
        payment_details: paymentForm.method === 'paypal'
          ? paymentForm.paypalEmail
          : { name: paymentForm.bankName, account: paymentForm.bankAccount, sortCode: paymentForm.bankSort }
      }
      setSelectedDeal(newDeal)

      setSettingPayment(false)
      setPaymentForm({ method: '', paypalEmail: '', bankName: '', bankAccount: '', bankSort: '' })
      if (onDealUpdated) onDealUpdated()
      alert('Payment details saved')
    } catch (error) {
      alert(`Error saving payment details: ${error.message}`)
    } finally {
      setSavingPayment(false)
    }
  }

  // Load deal images from R2
  async function loadDealImages() {
    if (!selectedDeal?.id) return

    setLoadingDealImages(true)
    try {
      const result = await api.get(`/api/deals/${selectedDeal.id}/images`)
      setDealImages(result.images || [])
    } catch (error) {
      console.error('Error loading images:', error)
    } finally {
      setLoadingDealImages(false)
    }
  }

  // Toggle deal images
  function toggleDealImages() {
    if (dealImagesExpanded) {
      setDealImagesExpanded(false)
    } else {
      setDealImagesExpanded(true)
      if (dealImages.length === 0) {
        loadDealImages()
      }
    }
  }

  // Update a single item's SP or BP in parsed_items and save
  async function updateItemPrice(itemIndex, field, value) {
    const items = parseItems(selectedDeal?.parsed_items)
    if (!items[itemIndex]) return

    const numValue = parseFloat(value) || 0
    let line = items[itemIndex]
    const regex = field === 'sp' ? /\bSP[-:.]?\s*£?[\d,.]+/i : /\bBP[-:.]?\s*£?[\d,.]+/i
    const replacement = field === 'sp' ? `SP:£${numValue}` : `BP:£${numValue}`

    if (regex.test(line)) {
      // Replace existing price (normalises non-standard formats like "SP-200" to "SP:£200")
      line = line.replace(regex, replacement)
    } else {
      line = line.replace(/\s*$/, ` - ${replacement}`)
    }

    items[itemIndex] = line
    const newParsedItems = items.join('\n')

    try {
      await api.patch(`/api/deals/${selectedDeal.id}`, { parsed_items: newParsedItems })
      setSelectedDeal(prev => ({ ...prev, parsed_items: newParsedItems }))
      if (onDealUpdated) onDealUpdated()
    } catch (error) {
      alert(`Failed to update price: ${error.message}`)
    }
  }

  // Render parsed items for modal with structured display
  function renderModalParsedItems() {
    const isEditable = EDITABLE_STAGES.includes(selectedDeal?.dealstage)
    const items = parseItems(selectedDeal?.parsed_items)
    if (items.length === 0) return <span className="text-muted">No items</span>

    return (
      <div style={{ fontSize: '0.85rem' }}>
        {items.map((itemLine, index) => {
          const item = parseItemLine(itemLine)
          const urls = getSearchUrls(itemLine)

          if (!item) return null

          return (
            <div key={index} style={{
              marginBottom: 12,
              padding: 12,
              background: 'var(--color-bg-secondary)',
              borderRadius: 8,
              border: '1px solid var(--color-border)'
            }}>
              {/* Item Header */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                marginBottom: 8
              }}>
                <span style={{
                  background: 'var(--color-primary)',
                  color: 'var(--color-charcoal)',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  flexShrink: 0
                }}>
                  {index + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>{item.name}</div>
                  {item.details && (
                    <div style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>{item.details}</div>
                  )}
                </div>
              </div>

              {/* Qty, SP, BP boxes */}
              <div style={{
                display: 'flex',
                gap: 8,
                marginBottom: 8,
                flexWrap: 'wrap'
              }}>
                <div style={{
                  background: 'var(--color-bg)',
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  textAlign: 'center',
                  minWidth: 50
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Qty</div>
                  <div style={{ fontWeight: 600 }}>{item.qty || 1}</div>
                </div>
                <div style={{
                  background: 'var(--color-bg)',
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  textAlign: 'center',
                  minWidth: 70
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Sell</div>
                  {isEditable ? (
                    <input
                      type="number"
                      defaultValue={item.sp || 0}
                      onBlur={e => updateItemPrice(index, 'sp', e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                      style={{
                        width: 70, textAlign: 'center', fontWeight: 600,
                        color: 'var(--color-success)', background: 'transparent',
                        border: '1px solid var(--color-border)', borderRadius: 4,
                        padding: '2px 4px', fontSize: '0.85rem'
                      }}
                    />
                  ) : (
                    <div style={{ fontWeight: 600, color: 'var(--color-success)' }}>£{item.sp?.toFixed(2) || '0.00'}</div>
                  )}
                </div>
                <div style={{
                  background: 'var(--color-bg)',
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)',
                  textAlign: 'center',
                  minWidth: 70
                }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>Buy</div>
                  {isEditable ? (
                    <input
                      type="number"
                      defaultValue={item.bp || 0}
                      onBlur={e => updateItemPrice(index, 'bp', e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && e.target.blur()}
                      style={{
                        width: 70, textAlign: 'center', fontWeight: 600,
                        color: 'var(--color-warning)', background: 'transparent',
                        border: '1px solid var(--color-border)', borderRadius: 4,
                        padding: '2px 4px', fontSize: '0.85rem'
                      }}
                    />
                  ) : (
                    <div style={{ fontWeight: 600, color: 'var(--color-warning)' }}>£{item.bp?.toFixed(2) || '0.00'}</div>
                  )}
                </div>
              </div>

              {/* Search buttons */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.reverbSold, '_blank')} title="Reverb Sold">RS</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.reverbActive, '_blank')} title="Reverb Active">RA</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.ebayActive, '_blank')} title="eBay Active">EA</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.ebaySold, '_blank')} title="eBay Sold">ES</button>
                <button className="btn btn-xs btn-secondary" onClick={() => window.open(urls.google, '_blank')} title="Google">G</button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  if (!selectedDeal) return null

  return (
    <>
      {/* Deal Detail Modal */}
      <div className="modal-overlay" onClick={handleClose}>
        <div
          className="modal-content modal-large"
          onClick={e => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflow: 'auto' }}
        >
          <div className="modal-header">
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>Deal Name</div>
              <h2 style={{ margin: 0 }}>{selectedDeal.dealname}</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <PinButton
                entityType="deal"
                entityId={selectedDeal.id}
                entityName={selectedDeal.dealname || selectedDeal.customer_name}
              />
              <button className="modal-close" onClick={handleClose}>&times;</button>
            </div>
          </div>
          <div className="modal-body-scroll">
            {/* Deal info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
              {/* Left column */}
              <div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Deal ID</label>
                  <div>{selectedDeal.id}</div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Customer</label>
                  <div>{selectedDeal.customer_name || '-'}</div>
                  {(selectedDeal.seller_email || selectedDeal.contact_email) && (
                    <div>
                      <button
                        type="button"
                        className={`email-link-btn ${expandedEmailDealId === selectedDeal.id ? 'active' : ''}`}
                        onClick={() => toggleEmailExpand(selectedDeal.id, selectedDeal.seller_email || selectedDeal.contact_email)}
                      >
                        {selectedDeal.seller_email || selectedDeal.contact_email}
                        <span style={{ marginLeft: 4, fontSize: '0.7rem' }}>{expandedEmailDealId === selectedDeal.id ? '▲' : '▼'}</span>
                      </button>

                      {/* Email list dropdown */}
                      {expandedEmailDealId === selectedDeal.id && (
                        <div className="email-list-dropdown">
                          {loadingCustomerEmails[selectedDeal.id] ? (
                            <div className="loading-spinner loading-spinner-sm" style={{ margin: '8px auto', display: 'block' }} />
                          ) : customerEmails[selectedDeal.id]?.length > 0 ? (
                            <div className="email-list">
                              {customerEmails[selectedDeal.id].map((email, idx) => (
                                <div
                                  key={email.messageId || idx}
                                  className="email-list-item"
                                  onClick={() => fetchEmailDetail(email.messageId, email.source)}
                                >
                                  <div className="email-item-header">
                                    <span className={`email-badge email-badge-${email.source}`}>
                                      {email.source === 'postmark' ? 'Postmark' : email.source}
                                    </span>
                                    <span className={`email-direction email-direction-${email.direction}`}>
                                      {email.direction === 'out' ? '→ Out' : '← In'}
                                    </span>
                                    {email.status && <span className="email-status">{email.status}</span>}
                                    {email.openCount > 0 && <span className="email-opens">{email.openCount} opens</span>}
                                    <span className="email-date">{formatDate(email.date)}</span>
                                  </div>
                                  <div className="email-subject">{email.subject}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted" style={{ padding: 8, textAlign: 'center', fontSize: '0.8rem' }}>
                              No emails found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {/* Create Ticket button */}
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openTicketModal(selectedDeal)}
                    style={{ marginTop: 8 }}
                  >
                    📧 Create Ticket
                  </button>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Created</label>
                  <div>{formatDate(selectedDeal.createdate)}</div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Value</label>
                  {isAdmin && editingValue ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <span style={{ fontSize: '0.9rem' }}>£</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-input"
                        value={valueInput}
                        onChange={e => setValueInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveValue(); if (e.key === 'Escape') setEditingValue(false) }}
                        autoFocus
                        style={{ width: '120px' }}
                      />
                      <button className="btn btn-sm btn-primary" onClick={saveValue} disabled={savingValue}>
                        {savingValue ? '...' : 'Save'}
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditingValue(false)} disabled={savingValue}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <span>{formatCurrency(selectedDeal.amount)}</span>
                      {isAdmin && (
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => { setValueInput(selectedDeal.amount || ''); setEditingValue(true) }}
                          style={{ padding: '2px 6px', fontSize: '0.7rem' }}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Right column */}
              <div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Stage</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <select
                      className="form-input"
                      value={selectedDeal.dealstage}
                      onChange={e => changeDealStage(e.target.value)}
                      disabled={changingStage}
                      style={{ flex: 1 }}
                    >
                      {DEAL_STAGES.filter(s => s.id).map(stage => (
                        <option key={stage.id} value={stage.id}>{stage.label}</option>
                      ))}
                    </select>
                    {changingStage && <div className="loading-spinner loading-spinner-sm" />}
                  </div>
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Date</label>
                  {editingCollectionDate ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                      <input
                        type="date"
                        className="form-input"
                        value={collectionDateValue}
                        onChange={e => setCollectionDateValue(e.target.value)}
                        style={{ flex: 1, fontSize: '0.85rem' }}
                      />
                      <button className="btn btn-primary" onClick={saveCollectionDate} disabled={savingCollectionDate} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                        {savingCollectionDate ? '...' : 'Save'}
                      </button>
                      <button className="btn" onClick={() => { setEditingCollectionDate(false); setCollectionDateValue(selectedDeal.collection_date || '') }} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{selectedDeal.collection_date ? formatDate(selectedDeal.collection_date) : '-'}</span>
                      {isAdmin && (
                        <button onClick={() => { setCollectionDateValue(selectedDeal.collection_date || ''); setEditingCollectionDate(true) }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted)', fontSize: '0.75rem' }} title="Edit collection date">
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="text-muted" style={{ fontSize: '0.75rem' }}>Tracking</label>
                  <div>
                    {selectedDeal.tracking_number ? (
                      <>
                        <span>{selectedDeal.carrier || ''} </span>
                        {selectedDeal.tracking_number.split(',').map((tn, idx) => {
                          const trimmed = tn.trim()
                          if (!trimmed) return null
                          const url = getTrackingUrl(trimmed, selectedDeal.carrier)
                          return (
                            <span key={idx}>
                              {idx > 0 && ', '}
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: 'var(--color-info)' }}
                                >
                                  {trimmed}
                                </a>
                              ) : (
                                <span>{trimmed}</span>
                              )}
                            </span>
                          )
                        })}
                      </>
                    ) : '-'}
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Details - Collapsible */}
            <div style={{
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setPaymentDetailsExpanded(!paymentDetailsExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                <span>Payment Details</span>
                <span style={{
                  transform: paymentDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>▼</span>
              </button>
              {paymentDetailsExpanded && (() => {
                const hasPayPal = selectedDeal.payment_paypal_email && selectedDeal.payment_paypal_email.trim() !== ''
                const hasBank = selectedDeal.payment_bank_account && selectedDeal.payment_bank_account.trim() !== ''
                const paymentMethod = hasPayPal ? 'paypal' : hasBank ? 'bank' : null

                return (
                <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                    <div>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>Payment Method</label>
                      <div style={{ marginTop: 4 }}>
                        {paymentMethod === 'paypal' && (
                          <span className="badge badge-info">PayPal</span>
                        )}
                        {paymentMethod === 'bank' && (
                          <span className="badge badge-secondary">Bank Transfer</span>
                        )}
                        {!paymentMethod && (
                          <span className="badge badge-warning">Not Set</span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>Payment Status</label>
                      <div style={{ marginTop: 4 }}>
                        {selectedDeal.payment_status === 'paid' ? (
                          <span className="badge badge-success">Paid</span>
                        ) : selectedDeal.payment_status === 'pending' ? (
                          <span className="badge badge-warning">Pending</span>
                        ) : (
                          <span className="badge">Awaiting</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Payment Details based on method */}
                  {paymentMethod === 'paypal' && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>PayPal Email</label>
                      <div style={{
                        marginTop: 4,
                        padding: 'var(--space-2) var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 6,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem'
                      }}>
                        {selectedDeal.payment_paypal_email}
                      </div>
                    </div>
                  )}
                  {paymentMethod === 'bank' && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <label className="text-muted" style={{ fontSize: '0.75rem' }}>Bank Details</label>
                      <div style={{
                        marginTop: 4,
                        padding: 'var(--space-3)',
                        background: 'var(--color-bg-secondary)',
                        borderRadius: 6,
                        fontSize: '0.85rem'
                      }}>
                        <div style={{ marginBottom: 4 }}><strong>Name:</strong> {selectedDeal.payment_bank_name || '-'}</div>
                        <div style={{ marginBottom: 4 }}><strong>Account:</strong> {selectedDeal.payment_bank_account || '-'}</div>
                        <div><strong>Sort Code:</strong> {selectedDeal.payment_bank_sort || '-'}</div>
                      </div>
                    </div>
                  )}
                  {!settingPayment && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      {!paymentMethod && (
                        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginBottom: 'var(--space-3)' }}>
                          No payment method has been set for this deal.
                        </p>
                      )}
                      <button
                        className={`btn btn-sm ${paymentMethod ? 'btn-secondary' : 'btn-primary'}`}
                        onClick={() => {
                          if (paymentMethod) {
                            // Pre-populate form with existing values
                            setPaymentForm({
                              method: paymentMethod,
                              paypalEmail: selectedDeal.payment_paypal_email || '',
                              bankName: selectedDeal.payment_bank_name || '',
                              bankAccount: selectedDeal.payment_bank_account || '',
                              bankSort: selectedDeal.payment_bank_sort || ''
                            })
                          }
                          setSettingPayment(true)
                        }}
                      >
                        {paymentMethod ? 'Edit Payment Details' : 'Set Payment Method'}
                      </button>
                    </div>
                  )}
                  {/* Payment Setup Form */}
                  {settingPayment && (
                    <div style={{ marginTop: 'var(--space-3)' }}>
                      <div style={{ marginBottom: 'var(--space-3)' }}>
                        <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                          Select Payment Method
                        </label>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button
                            className={`btn btn-sm ${paymentForm.method === 'paypal' ? 'btn-info' : 'btn-secondary'}`}
                            onClick={() => setPaymentForm(prev => ({ ...prev, method: 'paypal' }))}
                            style={paymentForm.method === 'paypal' ? { background: '#0070ba' } : {}}
                          >
                            PayPal
                          </button>
                          <button
                            className={`btn btn-sm ${paymentForm.method === 'bank' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setPaymentForm(prev => ({ ...prev, method: 'bank' }))}
                          >
                            Bank Transfer
                          </button>
                        </div>
                      </div>

                      {/* PayPal Form */}
                      {paymentForm.method === 'paypal' && (
                        <div style={{ marginBottom: 'var(--space-3)' }}>
                          <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                            PayPal Email Address
                          </label>
                          <input
                            type="email"
                            className="form-input"
                            placeholder="customer@email.com"
                            value={paymentForm.paypalEmail}
                            onChange={e => setPaymentForm(prev => ({ ...prev, paypalEmail: e.target.value }))}
                            style={{ maxWidth: 300 }}
                          />
                        </div>
                      )}

                      {/* Bank Form */}
                      {paymentForm.method === 'bank' && (
                        <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                              Account Holder Name
                            </label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="John Smith"
                              value={paymentForm.bankName}
                              onChange={e => setPaymentForm(prev => ({ ...prev, bankName: e.target.value }))}
                              style={{ maxWidth: 300 }}
                            />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', maxWidth: 300 }}>
                            <div>
                              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                                Sort Code
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                placeholder="12-34-56"
                                value={paymentForm.bankSort}
                                onChange={e => setPaymentForm(prev => ({ ...prev, bankSort: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>
                                Account Number
                              </label>
                              <input
                                type="text"
                                className="form-input"
                                placeholder="12345678"
                                value={paymentForm.bankAccount}
                                onChange={e => setPaymentForm(prev => ({ ...prev, bankAccount: e.target.value }))}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {paymentForm.method && (
                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setSettingPayment(false)
                              setPaymentForm({ method: '', paypalEmail: '', bankName: '', bankAccount: '', bankSort: '' })
                            }}
                            disabled={savingPayment}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-success btn-sm"
                            onClick={savePaymentDetails}
                            disabled={savingPayment}
                          >
                            {savingPayment ? 'Saving...' : 'Save Payment Details'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )})()}
            </div>

            {/* Collection Details - Collapsible */}
            <div style={{
              marginBottom: 'var(--space-4)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              overflow: 'hidden'
            }}>
              <button
                onClick={() => setAddressDetailsExpanded(!addressDetailsExpanded)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 'var(--space-3)',
                  background: 'var(--color-bg-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              >
                <span>Collection Details</span>
                <span style={{
                  transform: addressDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>▼</span>
              </button>
              {addressDetailsExpanded && (() => {
                const hasAddress = selectedDeal.collection_address && selectedDeal.collection_address.trim() !== ''
                const hasPhone = selectedDeal.collection_phone && selectedDeal.collection_phone.trim() !== ''
                const hasSendType = selectedDeal.send_type && selectedDeal.send_type.trim() !== ''
                const hasPackageDetails = selectedDeal.package_details && selectedDeal.package_details.trim() !== ''
                const hasCollectionDate = selectedDeal.collection_date
                const hasAnyDetails = hasAddress || hasPhone || hasSendType || hasPackageDetails || hasCollectionDate

                return (
                  <div style={{ padding: 'var(--space-3)', background: 'var(--color-bg)' }}>
                    {hasAnyDetails ? (
                      <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                        {/* Send Type & Collection Date Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Send Type</label>
                            <div style={{ marginTop: 4 }}>
                              {hasSendType ? (
                                <span className={`badge ${selectedDeal.send_type === 'collection' ? 'badge-info' : 'badge-secondary'}`}>
                                  {selectedDeal.send_type === 'collection' ? 'Collection' : selectedDeal.send_type === 'dropoff' ? 'Drop Off' : selectedDeal.send_type}
                                </span>
                              ) : (
                                <span className="badge badge-warning">Not Set</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Date</label>
                            <div style={{ marginTop: 4, fontSize: '0.85rem' }}>
                              {editingCollectionDate ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <input type="date" className="form-input" value={collectionDateValue} onChange={e => setCollectionDateValue(e.target.value)} style={{ padding: '4px 8px', fontSize: '0.85rem', width: 160 }} />
                                  <button className="btn btn-primary" onClick={saveCollectionDate} disabled={savingCollectionDate} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                    {savingCollectionDate ? '...' : 'Save'}
                                  </button>
                                  <button className="btn" onClick={() => { setEditingCollectionDate(false); setCollectionDateValue(selectedDeal.collection_date || '') }} style={{ padding: '4px 10px', fontSize: '0.75rem' }}>
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span>{hasCollectionDate ? formatDate(selectedDeal.collection_date) : '-'}</span>
                                  {isAdmin && (
                                    <button onClick={() => { setCollectionDateValue(selectedDeal.collection_date || ''); setEditingCollectionDate(true) }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--muted)', fontSize: '0.75rem' }} title="Edit collection date">
                                      Edit
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Address */}
                        {hasAddress && (
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Collection Address</label>
                            <div style={{
                              marginTop: 4,
                              padding: 'var(--space-2) var(--space-3)',
                              background: 'var(--color-bg-secondary)',
                              borderRadius: 6,
                              fontSize: '0.85rem',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {selectedDeal.collection_address}
                            </div>
                          </div>
                        )}
                        {/* Phone */}
                        {hasPhone && (
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Phone Number</label>
                            <div style={{
                              marginTop: 4,
                              padding: 'var(--space-2) var(--space-3)',
                              background: 'var(--color-bg-secondary)',
                              borderRadius: 6,
                              fontSize: '0.85rem'
                            }}>
                              <a href={`tel:${selectedDeal.collection_phone}`} style={{ color: 'var(--color-info)' }}>
                                {selectedDeal.collection_phone}
                              </a>
                            </div>
                          </div>
                        )}
                        {/* Package Details */}
                        {hasPackageDetails && (
                          <div>
                            <label className="text-muted" style={{ fontSize: '0.75rem' }}>Package Details</label>
                            <div style={{
                              marginTop: 4,
                              padding: 'var(--space-2) var(--space-3)',
                              background: 'var(--color-bg-secondary)',
                              borderRadius: 6,
                              fontSize: '0.85rem',
                              whiteSpace: 'pre-wrap'
                            }}>
                              {selectedDeal.package_details}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                        No collection details have been set for this deal.
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>

            {/* Parsed Items */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <h4 style={{ margin: 0 }}>Items</h4>
                {EDITABLE_STAGES.includes(selectedDeal.dealstage) && (
                  <button
                    className="btn btn-xs btn-secondary"
                    onClick={() => {
                      if (editingParsedItems) {
                        setEditingParsedItems(false)
                        setParsedItemsValue(selectedDeal.parsed_items || '')
                      } else {
                        setEditingParsedItems(true)
                      }
                    }}
                  >
                    {editingParsedItems ? 'Cancel' : 'Edit'}
                  </button>
                )}
              </div>
              {editingParsedItems ? (
                <div>
                  <textarea
                    className="form-input"
                    rows={8}
                    value={parsedItemsValue}
                    onChange={e => setParsedItemsValue(e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                  <div style={{ marginTop: 'var(--space-2)' }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={saveParsedItems}
                      disabled={savingParsedItems}
                    >
                      {savingParsedItems ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                renderModalParsedItems()
              )}
            </div>

            {/* Deal Images */}
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <button
                className="btn btn-sm btn-secondary"
                onClick={toggleDealImages}
                style={{ marginBottom: 'var(--space-2)' }}
              >
                {dealImagesExpanded ? 'Hide Images' : 'Show Images'}
              </button>
              {dealImagesExpanded && (
                <div>
                  {loadingDealImages ? (
                    <div className="loading-spinner" />
                  ) : dealImages.length > 0 ? (
                    <div className="jotform-images-grid">
                      {dealImages.map((url, i) => (
                        <div
                          key={i}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openLightbox(dealImages, i)}
                        >
                          <img
                            src={url}
                            alt={`Image ${i + 1}`}
                            className="jotform-image"
                            style={{ pointerEvents: 'none' }}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">No images found</p>
                  )}
                </div>
              )}
            </div>

            {/* Pricing Log */}
            {(selectedDeal.claude_pricing_log || editingPricingLog) && (
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setPricingLogExpanded(!pricingLogExpanded)}
                  >
                    {pricingLogExpanded ? 'Hide Pricing Log' : 'Show Pricing Log'}
                  </button>
                  {pricingLogExpanded && EDITABLE_STAGES.includes(selectedDeal?.dealstage) && !editingPricingLog && (
                    <>
                      <button
                        className="btn btn-xs btn-secondary"
                        onClick={() => {
                          setEditingPricingLog(true)
                          setPricingLogValue(selectedDeal.claude_pricing_log || '')
                        }}
                      >
                        Edit
                      </button>
                      {selectedDeal.claude_pricing_log && (
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedDeal.claude_pricing_log)
                            alert('Pricing log copied to clipboard!')
                          }}
                        >
                          Copy
                        </button>
                      )}
                    </>
                  )}
                  {pricingLogExpanded && editingPricingLog && (
                    <>
                      <button
                        className="btn btn-xs btn-primary"
                        onClick={savePricingLog}
                        disabled={savingPricingLog}
                      >
                        {savingPricingLog ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        className="btn btn-xs btn-secondary"
                        onClick={() => { setEditingPricingLog(false); setPricingLogValue('') }}
                        disabled={savingPricingLog}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
                {pricingLogExpanded && (
                  editingPricingLog ? (
                    <textarea
                      className="form-input"
                      rows={12}
                      value={pricingLogValue}
                      onChange={e => setPricingLogValue(e.target.value)}
                      style={{
                        marginTop: 'var(--space-2)',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        width: '100%'
                      }}
                      autoFocus
                    />
                  ) : (
                    <pre style={{
                      marginTop: 'var(--space-2)',
                      padding: 'var(--space-3)',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 4,
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 300,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedDeal.claude_pricing_log}
                    </pre>
                  )
                )}
              </div>
            )}

            {/* Collection Link */}
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label className="text-muted" style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                Collection Link
                {isAdmin && !editingCollectionLink && (
                  <>
                    <button
                      className="btn btn-xs btn-secondary"
                      onClick={() => { setCollectionLinkValue(selectedDeal.collection_link || ''); setEditingCollectionLink(true) }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-xs btn-secondary"
                      onClick={regenerateCollectionToken}
                      disabled={regeneratingToken}
                    >
                      {regeneratingToken ? 'Regenerating...' : 'Regenerate Token'}
                    </button>
                  </>
                )}
              </label>
              {editingCollectionLink ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={collectionLinkValue}
                    onChange={e => setCollectionLinkValue(e.target.value)}
                    placeholder="https://..."
                    style={{ flex: 1, padding: '4px 8px', fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: 4 }}
                  />
                  <button className="btn btn-sm btn-primary" onClick={saveCollectionLink} disabled={savingCollectionLink}>
                    {savingCollectionLink ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setEditingCollectionLink(false)}>Cancel</button>
                </div>
              ) : selectedDeal.collection_link ? (
                <div>
                  <a href={selectedDeal.collection_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                    {selectedDeal.collection_link}
                  </a>
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Not set</div>
              )}
            </div>

            {/* Label Link */}
            {selectedDeal.label_link && (
              <div style={{ marginBottom: 'var(--space-3)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem' }}>Label Link</label>
                <div>
                  <a href={selectedDeal.label_link} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: '0.85rem' }}>
                    {selectedDeal.label_link}
                  </a>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: 'var(--space-4)' }}>
            <button className="btn btn-secondary" onClick={handleClose}>Close</button>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox.open && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>&times;</button>

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&#8249;</button>
            )}

            <img
              src={lightbox.images[lightbox.currentIndex]}
              alt={`Image ${lightbox.currentIndex + 1}`}
              className="lightbox-image"
            />

            {lightbox.images.length > 1 && (
              <button className="lightbox-nav lightbox-next" onClick={nextImage}>&#8250;</button>
            )}

            <div className="lightbox-counter">
              {lightbox.currentIndex + 1} / {lightbox.images.length}
            </div>

            {lightbox.images.length > 1 && (
              <div className="lightbox-thumbnails">
                {lightbox.images.map((url, idx) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Thumbnail ${idx + 1}`}
                    className={`lightbox-thumb ${idx === lightbox.currentIndex ? 'active' : ''}`}
                    onClick={() => setLightbox(prev => ({ ...prev, currentIndex: idx }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {emailDetailModal && (
        <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={() => setEmailDetailModal(null)}>
          <div className="modal-content" style={{ maxWidth: 700, maxHeight: '80vh' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>{emailDetailModal.subject}</h3>
              <button className="modal-close" onClick={() => setEmailDetailModal(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll" style={{ padding: 'var(--space-4)' }}>
              <div style={{ marginBottom: 'var(--space-3)', fontSize: '0.85rem' }}>
                <div><strong>From:</strong> {emailDetailModal.from}</div>
                <div><strong>To:</strong> {emailDetailModal.to}</div>
                <div><strong>Date:</strong> {formatDate(emailDetailModal.date)}</div>
                <div><strong>Source:</strong> {emailDetailModal.source}</div>
              </div>
              <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
                {emailDetailModal.htmlBody ? (
                  <iframe
                    srcDoc={emailDetailModal.htmlBody}
                    style={{
                      width: '100%',
                      minHeight: 400,
                      border: '1px solid var(--color-border)',
                      borderRadius: 4,
                      background: 'white'
                    }}
                    sandbox="allow-same-origin"
                    title="Email content"
                  />
                ) : (
                  <pre style={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'inherit',
                    fontSize: '0.85rem',
                    margin: 0
                  }}>
                    {emailDetailModal.textBody || 'No content'}
                  </pre>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEmailDetailModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showTicketModal && ticketDeal && (
        <div className="modal-overlay" onClick={() => { setShowTicketModal(false); setTicketDeal(null); }}>
          <div
            className="modal-content modal-large"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: 800, width: '90%' }}
          >
            <div className="modal-header">
              <h2>Create Ticket</h2>
              <button className="modal-close" onClick={() => { setShowTicketModal(false); setTicketDeal(null); }}>&times;</button>
            </div>
            <div className="modal-body" style={{ padding: '20px 24px' }}>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>To</label>
                <div style={{
                  padding: '10px 14px',
                  background: 'var(--color-bg)',
                  borderRadius: 6,
                  border: '1px solid var(--color-border)'
                }}>
                  {ticketDeal.customer_name && <strong>{ticketDeal.customer_name}</strong>}
                  {ticketDeal.customer_name && ' '}
                  &lt;{ticketDeal.seller_email || ticketDeal.contact_email}&gt;
                </div>
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>Subject</label>
                <input
                  type="text"
                  className="form-input"
                  value={ticketSubject}
                  onChange={e => setTicketSubject(e.target.value)}
                  placeholder="Email subject..."
                  style={{ padding: '10px 14px' }}
                />
              </div>
              <div style={{ marginBottom: 'var(--space-4)' }}>
                <label className="text-muted" style={{ fontSize: '0.75rem', display: 'block', marginBottom: 6 }}>Message</label>
                <textarea
                  className="form-input"
                  rows={15}
                  value={ticketMessage}
                  onChange={e => setTicketMessage(e.target.value)}
                  placeholder="Type your message here..."
                  style={{ resize: 'vertical', minHeight: 300, padding: '12px 14px' }}
                />
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                This will send an email to the customer and create a ticket in pending status.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setShowTicketModal(false); setTicketDeal(null); }}
                disabled={sendingTicket}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={sendTicket}
                disabled={sendingTicket || !ticketSubject || !ticketMessage}
              >
                {sendingTicket ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DealDetailModal
